import React, { useState, useMemo, useEffect, useRef } from "react";
import { Check, ArrowRight, ExternalLink, Sparkles, Award, Wrench, TrendingDown } from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";
import { RoiCalculator } from "../components/RoiCalculator";
import { PricingCalculator } from "../components/PricingCalculator";
import { FaqSection } from "../components/FaqSection";
import {
    CURRENCIES,
    formatAmount,
    pickAmount,
    detectCurrencyFromLocale,
    detectCurrencyFromIp,
} from "../lib/currency";
import { PRODUCTS } from "../lib/products";

const STORAGE_KEY = "scitus.currency";
const BILLING_STORAGE_KEY = "scitus.billing";
const VALID_CODES = new Set(CURRENCIES.map((c) => c.code));
const BUNDLE_DISCOUNT_PCT = 25;
const ANNUAL_DISCOUNT_PCT = 20;

const readStoredCurrency = () => {
    try {
        if (typeof window === "undefined") return null;
        const v = window.localStorage.getItem(STORAGE_KEY);
        return v && VALID_CODES.has(v) ? v : null;
    } catch {
        return null;
    }
};

const readStoredBilling = () => {
    try {
        if (typeof window === "undefined") return "annual";
        const v = window.localStorage.getItem(BILLING_STORAGE_KEY);
        return v === "monthly" || v === "annual" ? v : "annual";
    } catch {
        return "annual";
    }
};

// Per-product pricing. Both INR and USD are explicit (from the pricing
// framework) — they are NOT mathematical conversions of one another. EUR is
// derived from USD at display time. Annual price is 20% off monthly per
// product. Bundle discount of 25% stacks multiplicatively on top of annual.
//
// `tiers` (optional) lets a product expose volume tiering on its card. Each
// tier is { name, range, customQuote? }. When `customQuote` is true the tier
// shows "Custom quoted — talk to us" instead of the per-unit price and links
// to /contact.
export const PRODUCT_PRICING = {
    mativex: {
        unit: "store",
        monthly: { INR: 12000, USD: 149 },
        annual:  { INR:  9600, USD: 119 },
        billed: { store: "Per store, billed monthly" },
        tiers: [
            { name: "Starter",   range: "1–3 stores",   note: "Per-store rate applies" },
            { name: "Growth",    range: "4–15 stores",  note: "Per-store rate applies" },
            { name: "Enterprise",range: "16+ stores · or 1 high-volume location", customQuote: true },
        ],
        includes: [
            "Real-time dashboards across every connected source",
            "Drill-down: group → entity → region → store → SKU",
            "Threshold alerts to Slack, Teams, or WhatsApp",
            "Up to 10 dashboards · 5 users · 3 data sources per store (Starter & Growth). Above 15 stores or 1 high-transaction-volume location, talk to sales for an Enterprise quote.",
        ],
    },
    vexen: {
        unit: "project",
        monthly: { INR: 18000, USD: 219 },
        annual:  { INR: 14400, USD: 175 },
        billed: { project: "Per active project, billed monthly" },
        tiers: [
            { name: "Starter / Growth", range: "1–5 active projects", note: "Per-project rate applies" },
            { name: "Enterprise",       range: "6+ active projects", customQuote: true },
        ],
        includes: [
            "Live project schedule, milestones, risk register",
            "Plant OEE / yield / scrap / downtime",
            "SLA & TAT tracking with auto-escalation",
            "Up to 5 active projects · 25 users (Starter / Growth). Above 5 active projects, talk to sales for an Enterprise quote.",
        ],
    },
    karya: {
        unit: "employee",
        monthly: { INR:   200, USD: 2.49 },
        annual:  { INR:   160, USD: 1.99 },
        includes: [
            "Attendance (geo, face, biometric, kiosk, mobile)",
            "Shift rosters, leave, regularisation, overtime",
            "Payroll, statutory filings, salary disbursement",
            "ESS portal · onboarding & offboarding flows",
        ],
    },
    vyaahar: {
        unit: "workspace",
        monthly: { INR: 10000, USD: 119 },
        annual:  { INR:  8000, USD: 95 },
        includes: [
            "Tax-compliant invoicing (GST / VAT / e-invoice)",
            "AR / AP / bank reconciliation with AI auto-match",
            "Multi-entity ledger with consolidation",
            "Up to 5 entities · 10 users",
        ],
    },
    attestra: {
        unit: "sender",
        monthly: { INR:  6000, USD: 74 },
        annual:  { INR:  4800, USD: 59 },
        includes: [
            "Up to 100 documents / sender / month",
            "Sequential & parallel signing workflows",
            "Tamper-evident audit trail + completion certificate",
            "REST API, webhooks, identity verification",
        ],
    },
    acrium: {
        unit: "project",
        monthly: { INR: 18000, USD: 219 },
        annual:  { INR: 14400, USD: 175 },
        includes: [
            "Live committed vs. budget vs. forecast",
            "Indent → RFQ → comparative → PO → GRN",
            "Vendor scorecards, price benchmarking",
            "Mobile site capture, offline-first",
        ],
    },
    kretara: {
        unit: "workspace",
        monthly: { INR: 12000, USD: 149 },
        annual:  { INR:  9600, USD: 119 },
        includes: [
            "Multi-level approval matrix by amount, category, store",
            "Vendor scorecards + price-vs-benchmark intelligence",
            "Role-based access at screen, action, and row granularity",
            "AI copilot grounded in your procurement history",
        ],
    },
};

const UNIT_LABEL_LONG = {
    workspace: "workspace",
    employee: "employee",
    sender: "sender",
    project: "active project",
    store: "store",
};

// Pre-packaged suites. Prices already include the 25% bundle discount.
// "Full Platform" intentionally omitted — Enterprise card handles that path.
const SUITES = [
    {
        slug: "retail",
        name: "Retail Suite",
        tagline: "Mativex + Vyaahar + Kretara + Attestra — for multi-location, multi-channel retail operators.",
        products: ["mativex", "vyaahar", "kretara", "attestra"],
        monthly: { INR: 30000, USD: 360 },
        annual:  { INR: 24000, USD: 288 },
        annualTotal: { INR: 288000, USD: 3456 },
    },
    {
        slug: "real-estate",
        name: "Real Estate Suite",
        tagline: "Vexen + Acrium + Attestra + Mativex — for developers running multi-project portfolios.",
        products: ["vexen", "acrium", "attestra", "mativex"],
        monthly: { INR: 40500, USD: 486 },
        annual:  { INR: 32400, USD: 389 },
        annualTotal: { INR: 388800, USD: 4668 },
    },
];

// Implementation & success package — waived on annual prepay.
const IMPLEMENTATION_TIERS = [
    {
        name: "Single product — standard",
        fee: { INR: 50000, USD: 599 },
        includes: "Standard connectors, 2 training sessions, 30-day hypercare",
    },
    {
        name: "Single product — complex ERP",
        fee: { INR_low: 75000, INR_high: 100000, USD_low: 899, USD_high: 1199 },
        feeKind: "range",
        includes: "Custom connector, data migration, 45-day hypercare",
    },
    {
        name: "Retail Suite",
        fee: { INR: 150000, USD: 1799 },
        includes: "Full suite config, ERP integration, training, 60-day hypercare",
    },
    {
        name: "Real Estate Suite",
        fee: { INR: 200000, USD: 2399 },
        includes: "Full suite config, ERP integration, training, 60-day hypercare",
    },
    {
        name: "Full Platform",
        fee: { INR: 300000, USD: 3599 },
        includes: "All 7 products, full ERP integration, multi-entity rollout, 90-day hypercare",
    },
    {
        name: "Annual prepay commitment",
        fee: null,
        includes: "Onboarding fee fully waived when you commit to an annual subscription.",
        waived: true,
    },
];

const COMPARISON_ROWS = [
    { feature: "Commitment", monthly: "None — cancel anytime", annual: "12 months prepaid" },
    { feature: "Saving vs monthly", monthly: "0%", annual: "20% off (+25% bundle = up to 40%)" },
    { feature: "Onboarding fee", monthly: "Full fee applies", annual: "Waived on commitment" },
    { feature: "Support channel", monthly: "Email", annual: "Email + WhatsApp" },
    { feature: "Response time", monthly: "8 business hours", annual: "4 business hours" },
    { feature: "Quarterly business review", monthly: "No", annual: "Yes — included" },
    { feature: "New feature early access", monthly: "No", annual: "Yes — included" },
    { feature: "Custom connector builds", monthly: "Chargeable extra", annual: "1 free per year" },
    { feature: "Training sessions", monthly: "2 included", annual: "4 included" },
    { feature: "SLA uptime guarantee", monthly: "99.5%", annual: "99.9%" },
    { feature: "Best for", monthly: "Try before committing", annual: "Committed operators" },
];

// Annual prepay ROI callout — Retail Suite Year-1 trade-off.
const ANNUAL_ROI_OPTIONS = [
    {
        label: "Monthly subscription + onboarding fee",
        upfront: { INR: 150000, USD: 1799 },
        monthly: { INR: 30000, USD: 360 },
        total: { INR: 510000, USD: 6120 },
        best: false,
    },
    {
        label: "Annual prepay — onboarding waived",
        upfront: { INR: 288000, USD: 3456 },
        monthly: null,
        total: { INR: 288000, USD: 3456 },
        best: true,
    },
    {
        label: "Annual prepay + keep onboarding",
        upfront: { INR: 150000, USD: 1799 },
        monthly: { INR: 288000, USD: 3456 },
        monthlyLabel: "Annual prepay",
        total: { INR: 438000, USD: 5255 },
        best: false,
    },
];

export const PRICING_FAQS = [
    {
        q: "How does the bundle discount work?",
        a: "Pick any single product at the listed price. The moment you take a second product, every product on your account drops 25% automatically — no coupons, no renegotiation. Add or drop products anytime; charges pro-rate to the day.",
    },
    {
        q: "What is the difference between Monthly and Annual?",
        a: "Monthly gives you full flexibility — cancel anytime, no commitment. Annual is prepaid for 12 months and saves you 20% on every product. Annual clients also get faster support (4-hour response vs 8-hour), quarterly business reviews, early access to new features, and one free custom connector build per year. Most clients who try Monthly move to Annual within 90 days.",
    },
    {
        q: "Is there a setup or onboarding fee?",
        a: "Yes — and we're upfront about it. Every deployment requires real engineering work: connecting your ERP or existing systems, configuring dashboards and workflows, migrating historical data, training your team, and providing hypercare for 30–90 days. Standard single-product onboarding starts at ₹50,000. Suite implementations range from ₹1,50,000 to ₹3,00,000. Commit to annual and we waive it entirely.",
    },
    {
        q: "Can I start with one product and add more later?",
        a: "Yes. Start with any single product. When you add a second, the 25% bundle discount applies automatically to everything on your account from that point forward. You do not need to renegotiate or restart your contract.",
    },
    {
        q: "Is there a free trial?",
        a: "We offer a structured 30-minute demo tailored to your industry and team size — not a generic product tour. After the demo, if it is a fit, we run a 14-day pilot on your actual data with a member of our team alongside yours. Book the demo at scitus.ai/contact.",
    },
    {
        q: "Do you work on top of existing ERP systems?",
        a: "Yes — this is by design. Mativex, Vexen, Acrium, and Kretara all sit on top of your existing ERP (SAP, Tally, Oracle, or custom). We do not replace your ERP. We replace the spreadsheets your team builds around it. Your ERP records transactions. Scitus tells you what they mean.",
    },
    {
        q: "What about Enterprise needs — SSO, white-label, custom data residency?",
        a: "Enterprise requirements are custom-quoted. So is anything that crosses a volume threshold — specifically, Mativex above 15 stores or with a single high-transaction-volume location, and Vexen above 5 active projects. Talk to us at hello@scitus.ai and we will respond within two business days.",
    },
    {
        q: "Are Mativex and Vexen prices flat, or do they scale with usage?",
        a: "Both are per-unit and tiered. Mativex is priced per store: the same per-store rate applies for the Starter (1–3 stores) and Growth (4–15 stores) tiers; above 15 stores — or a single location with high transaction volume — the Enterprise tier is custom-quoted. Vexen is priced per active project: the same per-project rate applies for 1–5 active projects (Starter/Growth); 6+ active projects move to a custom Enterprise quote. Karya, Vyaahar, Attestra, Acrium, and Kretara remain on flat per-unit pricing with no volume tier today.",
    },
    {
        q: "Can I switch between monthly and annual mid-cycle?",
        a: "Yes. Switching from monthly to annual is prorated — you pay the difference for the remaining period and move to annual immediately. Switching from annual to monthly is available at renewal.",
    },
];

const fmt = (amount, currency) => formatAmount(amount, currency);

const ImplementationSection = ({ currency }) => {
    const formatFee = (tier) => {
        if (tier.waived) return <span className="impl-waived">Waived</span>;
        if (!tier.fee) return "—";
        if (tier.feeKind === "range") {
            const lowKey = currency === "INR" ? "INR_low" : currency === "USD" ? "USD_low" : "USD_low";
            const highKey = currency === "INR" ? "INR_high" : currency === "USD" ? "USD_high" : "USD_high";
            const low = currency === "EUR" ? tier.fee.USD_low * 0.92 : tier.fee[lowKey];
            const high = currency === "EUR" ? tier.fee.USD_high * 0.92 : tier.fee[highKey];
            return `${fmt(low, currency)} – ${fmt(high, currency)}`;
        }
        return fmt(pickAmount(tier.fee, currency), currency);
    };

    return (
        <section
            className="section"
            data-testid="implementation-section"
            aria-labelledby="implementation-h2"
            style={{ paddingTop: 12 }}
        >
            <div className="section-head" style={{ textAlign: "center" }}>
                <span className="eyebrow">Implementation &amp; Success Package</span>
                <h2 id="implementation-h2" className="h2" style={{ marginTop: 14 }}>
                    Real work. Honestly priced. Waived when you commit.
                </h2>
                <p
                    className="muted"
                    style={{ maxWidth: 700, margin: "10px auto 0" }}
                >
                    Every Scitus deployment includes a structured onboarding —
                    connector build, data migration, configuration, training, and hypercare.
                    This is real work by our team. We charge for it honestly. Commit
                    to an annual subscription and we waive it entirely.
                </p>
            </div>
            <div className="impl-table-wrap" data-testid="impl-table">
                <table className="impl-table">
                    <thead>
                        <tr>
                            <th>Engagement</th>
                            <th>Fee</th>
                            <th>What&apos;s included</th>
                        </tr>
                    </thead>
                    <tbody>
                        {IMPLEMENTATION_TIERS.map((tier) => (
                            <tr
                                key={tier.name}
                                className={tier.waived ? "impl-row-waived" : ""}
                                data-testid={`impl-row-${tier.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <td className="impl-tier-name">
                                    {tier.waived ? <Award size={15} /> : <Wrench size={14} />}{" "}
                                    {tier.name}
                                </td>
                                <td className="impl-fee">{formatFee(tier)}</td>
                                <td className="impl-includes">{tier.includes}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p
                className="muted impl-table-caption"
                data-testid="impl-table-caption"
                style={{ maxWidth: 700, margin: "20px auto 0", textAlign: "center" }}
            >
                Most clients choose <strong style={{ color: "var(--accent)" }}>annual prepay</strong> —
                it&apos;s the lowest Year 1 cost and gives our team the certainty to deliver a
                quality implementation.
            </p>
        </section>
    );
};

const ComparisonSection = ({ currency }) => {
    const retailMonthly = fmt(pickAmount({ INR: 510000, USD: 6120 }, currency), currency);
    const retailAnnual = fmt(pickAmount({ INR: 288000, USD: 3456 }, currency), currency);
    const retailSaving = fmt(pickAmount({ INR: 222000, USD: 2664 }, currency), currency);

    return (
        <section
            className="section"
            data-testid="comparison-section"
            aria-labelledby="comparison-h2"
            style={{ paddingTop: 12 }}
        >
            <div className="section-head" style={{ textAlign: "center" }}>
                <span className="eyebrow">Monthly vs Annual</span>
                <h2 id="comparison-h2" className="h2" style={{ marginTop: 14 }}>
                    Both plans give you the full product. Annual gives you more.
                </h2>
            </div>
            <div className="cmp-table-wrap" data-testid="cmp-table">
                <table className="cmp-table">
                    <thead>
                        <tr>
                            <th>Feature</th>
                            <th>Monthly</th>
                            <th className="cmp-annual-col">Annual <span className="cmp-pill">−20%</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {COMPARISON_ROWS.map((row) => (
                            <tr
                                key={row.feature}
                                data-testid={`cmp-row-${row.feature.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <td className="cmp-feature">{row.feature}</td>
                                <td className="cmp-monthly">{row.monthly}</td>
                                <td className="cmp-annual">{row.annual}</td>
                            </tr>
                        ))}
                        <tr className="cmp-row-highlight">
                            <td className="cmp-feature">
                                <TrendingDown size={13} /> Retail Suite — Year 1 cost
                            </td>
                            <td className="cmp-monthly">{retailMonthly}</td>
                            <td className="cmp-annual">
                                <strong>{retailAnnual}</strong>{" "}
                                <span className="cmp-save"> save {retailSaving}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    );
};

const AnnualRoiCallout = ({ currency }) => {
    const fmtAmt = (priceObj) =>
        priceObj === null ? "—" : fmt(pickAmount(priceObj, currency), currency);

    return (
        <section
            className="section annual-roi-section"
            data-testid="annual-roi-callout"
            aria-labelledby="annual-roi-h2"
            style={{ paddingTop: 12 }}
        >
            <div className="annual-roi-card">
                <div className="annual-roi-head">
                    <span className="eyebrow" style={{ color: "var(--accent)" }}>
                        Annual prepay — Retail Suite Year 1
                    </span>
                    <h2 id="annual-roi-h2" className="h2" style={{ marginTop: 12 }}>
                        The lowest Year-1 cost &amp; the certainty to deliver.
                    </h2>
                </div>
                <div className="annual-roi-table-wrap">
                    <table className="annual-roi-table">
                        <thead>
                            <tr>
                                <th>Option</th>
                                <th>Upfront</th>
                                <th>Monthly</th>
                                <th>Total Year 1</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ANNUAL_ROI_OPTIONS.map((opt) => (
                                <tr
                                    key={opt.label}
                                    className={opt.best ? "annual-roi-best" : ""}
                                    data-testid={`annual-roi-row-${opt.label
                                        .toLowerCase()
                                        .replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <td className="annual-roi-label">
                                        {opt.best && <Award size={14} />}{" "}
                                        {opt.label}
                                        {opt.best && (
                                            <span className="annual-roi-badge">
                                                ✓ Best value
                                            </span>
                                        )}
                                    </td>
                                    <td>{fmtAmt(opt.upfront)}</td>
                                    <td>
                                        {opt.monthly ? (
                                            <>
                                                {fmtAmt(opt.monthly)}
                                                {opt.monthlyLabel ? (
                                                    <small> · {opt.monthlyLabel}</small>
                                                ) : (
                                                    <small> /mo</small>
                                                )}
                                            </>
                                        ) : (
                                            "—"
                                        )}
                                    </td>
                                    <td>
                                        <strong>{fmtAmt(opt.total)}</strong>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="annual-roi-caption">
                    Most clients choose <strong>Annual prepay with waived onboarding</strong>.
                    It is the lowest Year 1 cost and gives your team the certainty to deliver
                    a quality implementation.
                </p>
            </div>
        </section>
    );
};

export const PricingPage = () => {
    // Initial state: stored choice if any, else best-effort instant detection from locale.
    const [currency, setCurrency] = useState(
        () => readStoredCurrency() || detectCurrencyFromLocale()
    );
    const [billing, setBilling] = useState(() => readStoredBilling());
    const userChoseRef = useRef(Boolean(readStoredCurrency()));

    useEffect(() => {
        try { window.localStorage.setItem(STORAGE_KEY, currency); } catch { /* ignore */ }
    }, [currency]);

    useEffect(() => {
        try { window.localStorage.setItem(BILLING_STORAGE_KEY, billing); } catch { /* ignore */ }
    }, [billing]);

    useEffect(() => {
        if (userChoseRef.current) return undefined;
        const ctrl = new AbortController();
        detectCurrencyFromIp(ctrl.signal).then((code) => {
            if (code && !userChoseRef.current && code !== currency) {
                setCurrency(code);
            }
        });
        return () => ctrl.abort();
    }, []);

    const chooseCurrency = (code) => {
        userChoseRef.current = true;
        setCurrency(code);
    };

    const isAnnual = billing === "annual";

    // Product cards. Annual is the default presentation. Monthly price is
    // shown as a strikethrough when annual is active.
    const cards = useMemo(() => PRODUCTS.map((p) => {
        const price = PRODUCT_PRICING[p.slug];
        if (!price) return null;
        const monthlyAmt = pickAmount(price.monthly, currency);
        const annualAmt  = pickAmount(price.annual,  currency);
        const effective = isAnnual ? annualAmt : monthlyAmt;
        return {
            slug: p.slug,
            name: p.name,
            sub: p.sub,
            icon: p.icon,
            logo: p.logo,
            display: fmt(effective, currency),
            monthlyDisplay: fmt(monthlyAmt, currency),
            unit: price.unit,
            unitLabel: UNIT_LABEL_LONG[price.unit] || price.unit,
            includes: price.includes,
            tiers: price.tiers || null,
        };
    }).filter(Boolean), [currency, isAnnual]);
    const suiteCards = useMemo(() => SUITES.map((s) => ({
        ...s,
        display: fmt(pickAmount(isAnnual ? s.annual : s.monthly, currency), currency),
        monthlyDisplay: fmt(pickAmount(s.monthly, currency), currency),
        annualTotalDisplay: fmt(pickAmount(s.annualTotal, currency), currency),
        productNames: s.products.map(
            (slug) => PRODUCTS.find((p) => p.slug === slug)?.name || slug
        ),
    })), [currency, isAnnual]);

    return (
        <section className="section" id="pricing" data-testid="pricing-page">
            <div className="container">
                <div className="section-head">
                    <span className="eyebrow">Per-product pricing</span>
                    <h1 className="h1" style={{ marginTop: 18 }}>
                        Honest numbers. No &ldquo;Contact sales&rdquo; theatre.
                    </h1>
                    <p>
                        Start with one product or come in on a suite. Pay monthly for
                        flexibility or annually for savings. <strong>Bundle two or more
                        products and every product on your account drops 25%</strong> — automatically.
                    </p>

                    <div
                        style={{
                            marginTop: 28,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 16,
                            flexWrap: "wrap",
                        }}
                    >
                        <div
                            className="pricing-toggle"
                            data-testid="billing-toggle"
                            role="group"
                            aria-label="Billing cadence"
                        >
                            <button
                                type="button"
                                className={!isAnnual ? "active" : ""}
                                onClick={() => setBilling("monthly")}
                                data-testid="billing-toggle-monthly"
                            >
                                Monthly
                            </button>
                            <button
                                type="button"
                                className={isAnnual ? "active" : ""}
                                onClick={() => setBilling("annual")}
                                data-testid="billing-toggle-annual"
                            >
                                Annual
                                <span className="save">Save {ANNUAL_DISCOUNT_PCT}%</span>
                            </button>
                        </div>

                        <div
                            className="pricing-toggle currency-switcher"
                            data-testid="currency-switcher"
                            role="radiogroup"
                            aria-label="Currency"
                        >
                            {CURRENCIES.map((c) => (
                                <button
                                    key={c.code}
                                    type="button"
                                    role="radio"
                                    aria-checked={currency === c.code}
                                    className={currency === c.code ? "active" : ""}
                                    onClick={() => chooseCurrency(c.code)}
                                    data-testid={`currency-${c.code.toLowerCase()}`}
                                >
                                    <span className="currency-symbol">{c.symbol}</span>
                                    <span className="currency-code">{c.code}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Combined-saving banner */}
                <div className="bundle-banner" data-testid="bundle-banner">
                    <Sparkles size={16} />
                    <span>
                        <strong>Bundle 2+ products + Annual prepay = up to 40% off everything.</strong> Discounts stack automatically. No coupons, no contact-sales theatre.
                    </span>
                </div>

                <FadeUpGroup>
                    <div className="pricing-product-grid">
                        {cards.map((c) => {
                            const Icon = c.icon;
                            const hasLogo = c.logo && c.logo.src;
                            const logoFit = c.logo?.fit || "lockup";
                            const needsLightTile = c.logo?.needsLightTile === true;
                            return (
                                <div
                                    key={c.slug}
                                    className="price-card-product fade-up"
                                    data-testid={`price-card-${c.slug}`}
                                >
                                    <div className="price-card-head">
                                        {hasLogo ? (
                                            <img
                                                src={c.logo.src}
                                                alt={`${c.name} logo`}
                                                className={`price-card-logo price-card-logo--${logoFit}${needsLightTile ? " price-card-logo--lit" : ""}`}
                                                data-fit={logoFit}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        ) : (
                                            <div className="price-card-icon"><Icon size={22} /></div>
                                        )}
                                        <div className="price-card-meta">
                                            <div className="name">{c.name}</div>
                                            <div className="sub">{c.sub}</div>
                                        </div>
                                    </div>

                                    <div className="price-line">
                                        <span className="price" data-testid={`price-${c.slug}`}>{c.display}</span>
                                        <span className="period">/ {c.unitLabel} / mo</span>
                                    </div>
                                    {isAnnual && (
                                        <div className="price-strike" data-testid={`price-strike-${c.slug}`}>
                                            <s>{c.monthlyDisplay}</s>
                                            <span className="save-pct"> · Save {ANNUAL_DISCOUNT_PCT}%</span>
                                        </div>
                                    )}
                                    <div className="price-billed">
                                        Per {c.unitLabel}, billed {isAnnual ? "annually" : "monthly"}
                                    </div>

                                    {c.tiers && (
                                        <div
                                            className="price-tiers"
                                            data-testid={`price-tiers-${c.slug}`}
                                            aria-label={`${c.name} volume tiers`}
                                        >
                                            {c.tiers.map((t) => (
                                                <div
                                                    key={t.name}
                                                    className={`price-tier${t.customQuote ? " price-tier--custom" : ""}`}
                                                    data-testid={`price-tier-${c.slug}-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                >
                                                    <div className="price-tier-head">
                                                        <span className="price-tier-name">{t.name}</span>
                                                        <span className="price-tier-range">{t.range}</span>
                                                    </div>
                                                    {t.customQuote ? (
                                                        <a
                                                            href="/contact"
                                                            className="price-tier-cta"
                                                            data-testid={`price-tier-cta-${c.slug}`}
                                                        >
                                                            Custom quoted — talk to us <ArrowRight size={12} />
                                                        </a>
                                                    ) : (
                                                        <span className="price-tier-note">{t.note}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <ul className="price-includes">
                                        {c.includes.map((f) => (
                                            <li key={f}>
                                                <span className="check"><Check size={12} /></span>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="price-card-ctas">
                                        <a
                                            href={`/products/${c.slug}`}
                                            className="link-arrow"
                                            data-testid={`price-learn-${c.slug}`}
                                        >
                                            Learn more <ArrowRight size={14} />
                                        </a>
                                        <a
                                            href="/contact"
                                            className="btn btn-primary btn-sm"
                                            data-testid={`price-cta-${c.slug}`}
                                        >
                                            Start trial <ArrowRight size={14} />
                                        </a>
                                    </div>
                                    <p
                                        className="price-card-onboarding-note"
                                        data-testid={`price-onboarding-note-${c.slug}`}
                                    >
                                        One-time onboarding from {fmt(pickAmount({ INR: 50000, USD: 599 }, currency), currency)} ·{" "}
                                        <span className="onboarding-waived">Waived on annual commitment</span>
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </FadeUpGroup>

                {/* Pre-packaged suite cards */}
                <FadeUpGroup>
                    <div className="suite-section fade-up" data-testid="suite-section">
                        <div className="section-head" style={{ textAlign: "center" }}>
                            <span className="eyebrow">Pre-packaged suites</span>
                            <h2 className="h2" style={{ marginTop: 14 }}>
                                Two ready-to-run bundles, priced for the industries we know best.
                            </h2>
                            <p
                                className="muted"
                                style={{ maxWidth: 600, margin: "10px auto 0" }}
                            >
                                25% bundle discount already baked in. Pay annually and save another
                                20% — up to 40% off the individual product prices.
                            </p>
                        </div>
                        <div className="suite-grid" data-testid="suite-grid">
                            {suiteCards.map((s) => (
                                <article
                                    key={s.slug}
                                    className="suite-card"
                                    data-testid={`suite-card-${s.slug}`}
                                >
                                    <header className="suite-card-head">
                                        <h3 className="suite-card-name">{s.name}</h3>
                                        {isAnnual && (
                                            <span className="suite-badge">Save up to 40%</span>
                                        )}
                                    </header>
                                    <p className="suite-tagline">{s.tagline}</p>
                                    <div className="suite-products">
                                        {s.productNames.map((n) => (
                                            <span key={n} className="suite-product-chip">
                                                {n}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="suite-price-line">
                                        <span className="suite-price">{s.display}</span>
                                        <span className="suite-period">/ mo</span>
                                    </div>
                                    {isAnnual && (
                                        <div className="suite-strike">
                                            <s>{s.monthlyDisplay}/mo monthly</s>
                                            <span>
                                                {" "}
                                                · Year 1: {s.annualTotalDisplay}
                                            </span>
                                        </div>
                                    )}
                                    <a
                                        href="/contact"
                                        className="btn btn-primary btn-lg suite-cta"
                                        data-testid={`suite-cta-${s.slug}`}
                                    >
                                        Book a demo <ArrowRight size={14} />
                                    </a>
                                </article>
                            ))}
                        </div>
                    </div>
                </FadeUpGroup>

                {/* Enterprise strip */}
                <div className="enterprise-strip" data-testid="enterprise-strip">
                    <div>
                        <div className="eyebrow" style={{ marginBottom: 6 }}>Enterprise</div>
                        <h3 className="h3" style={{ fontSize: "1.4rem" }}>SSO, dedicated success, white-label, custom residency.</h3>
                        <p className="muted" style={{ marginTop: 8, maxWidth: 540 }}>
                            Multi-entity rollouts, complex ERP integrations, white-label deployments, and custom data residency are custom-quoted on top of standard product prices. Most quotes come back within two business days of an intro call. Minimum engagement: Full Platform annual subscription + Enterprise implementation package.
                        </p>
                    </div>
                    <a href="/contact" className="btn btn-ghost btn-lg" data-testid="enterprise-cta">
                        Talk to us <ExternalLink size={14} />
                    </a>
                </div>

                {/* Pricing calculator */}
                <FadeUpGroup>
                    <div className="fade-up">
                        <PricingCalculator
                            pricing={PRODUCT_PRICING}
                            currency={currency}
                            initialAnnual={isAnnual}
                            bundleDiscountPct={BUNDLE_DISCOUNT_PCT}
                            annualDiscountPct={ANNUAL_DISCOUNT_PCT}
                        />
                    </div>
                </FadeUpGroup>

                {/* Implementation & Success Package */}
                <ImplementationSection currency={currency} />

                {/* Monthly vs Annual comparison */}
                <ComparisonSection currency={currency} />

                {/* Annual prepay ROI callout */}
                <AnnualRoiCallout currency={currency} />

                {/* ROI calculator */}
                <FadeUpGroup>
                    <div className="fade-up">
                        <RoiCalculator currency={currency} />
                    </div>
                </FadeUpGroup>
            </div>

            <FaqSection
                heading="Pricing — frequently asked"
                items={PRICING_FAQS}
                testIdPrefix="faq-pricing"
            />
        </section>
    );
};

export default PricingPage;
